/**
 * ASCII Art Generator - Halftone 渲染引擎
 *
 * 使用不同形状的点阵模拟半调印刷效果
 * 支持动画渲染
 * Wave 8.3: 支持灰度预计算缓存
 */

import type {
  IRenderEngine,
  FullRenderSettings,
  RenderResult,
  StyleType,
} from '../types'
import type { AnimationContext } from '../animation/types'
import type { RenderContextExt } from './animationHelpers'
import {
  samplePixel,
  samplePixelFast,
  applyContrastAndBrightness,
  applyColorMode,
} from '../algorithms/grayscale'
import {
  calculateCharacterAnimation,
  applyPulseToCharacterBrightness,
  createStaticContext,
  hasGrayscaleCache,
} from './animationHelpers'

export class HalftoneEngine implements IRenderEngine {
  readonly name: StyleType = 'halftone'

  /**
   * 渲染图片为半调艺术（支持动画）
   * Wave 8.3: 支持灰度预计算缓存优化
   */
  render(
    source: HTMLImageElement | HTMLCanvasElement | ImageData,
    settings: FullRenderSettings,
    canvas: HTMLCanvasElement,
    context?: AnimationContext
  ): RenderResult {
    // 使用提供的动画上下文，或创建静态上下文
    const animContext: RenderContextExt = context ?? createStaticContext()

    const imageData = this.getImageData(source)
    const { width: imgWidth, height: imgHeight } = imageData

    const { halftoneShape, halftoneSize, halftoneRotation, contrast, brightness: brightnessSetting } = settings
    const cellSize = Math.max(4, halftoneSize * 10)

    const cols = Math.max(1, Math.floor(imgWidth / cellSize))
    const rows = Math.max(1, Math.floor(imgHeight / cellSize))

    canvas.width = cols * cellSize
    canvas.height = rows * cellSize

    const ctx = canvas.getContext('2d')!
    ctx.fillStyle = settings.backgroundColor
    ctx.fillRect(0, 0, canvas.width, canvas.height)

    // 应用旋转
    ctx.save()
    if (halftoneRotation !== 0) {
      const radians = (halftoneRotation * Math.PI) / 180
      ctx.translate(canvas.width / 2, canvas.height / 2)
      ctx.rotate(radians)
      ctx.translate(-canvas.width / 2, -canvas.height / 2)
    }

    // 检查动画或鼠标交互是否启用
    const animationEnabled = settings.ditherAnimationEnabled || settings.dither2AnimationEnabled
    const mouseInteractionEnabled = settings.ditherMouseInteractionEnabled || settings.dither2MouseInteractionEnabled
    const needsAnimation = animationEnabled || mouseInteractionEnabled

    // Wave 8.3: 检查是否有灰度缓存
    const useCache = hasGrayscaleCache(animContext)
    const grayscaleCache = animContext.grayscaleCache

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const sampleX = col * cellSize + cellSize / 2
        const sampleY = row * cellSize + cellSize / 2

        // Wave 8.3: 智能采样
        const { brightness: rawBrightness, color } = useCache && grayscaleCache
          ? samplePixelFast(grayscaleCache, col * cellSize, row * cellSize, cellSize, cellSize)
          : samplePixel(imageData, col * cellSize, row * cellSize, cellSize, cellSize)

        let finalBrightness = useCache
          ? rawBrightness
          : applyContrastAndBrightness(rawBrightness, contrast, brightnessSetting * 255)

        // 计算基础位置
        const baseX = col * cellSize
        const baseY = row * cellSize

        // 应用动画或鼠标交互效果
        let drawX = sampleX
        let drawY = sampleY

        if (needsAnimation) {
          const animResult = calculateCharacterAnimation(
            baseX, baseY, animContext, settings, cellSize, cellSize
          )

          drawX += animResult.offsetX
          drawY += animResult.offsetY

          // 应用脉动效果到亮度（仅动画）
          if (animationEnabled) {
            finalBrightness = applyPulseToCharacterBrightness(finalBrightness, animResult.pulseFactor)
          }
        }

        const normalizedBrightness = finalBrightness / 255

        // 应用颜色
        const finalColor = applyColorMode(color, settings.colorMode, settings.customColor)
        ctx.fillStyle = `rgb(${finalColor.r}, ${finalColor.g}, ${finalColor.b})`

        // 根据亮度绘制形状 (暗 = 大形状)
        const size = (1 - normalizedBrightness) * cellSize * 0.9
        if (size > 0.5) {
          this.drawShape(ctx, drawX, drawY, size, halftoneShape)
        }
      }
    }

    ctx.restore()

    return {
      canvas,
      width: canvas.width,
      height: canvas.height,
      charWidth: cellSize,
      charHeight: cellSize,
    }
  }

  private drawShape(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    size: number,
    shape: string
  ): void {
    ctx.beginPath()

    switch (shape) {
      case 'circle':
        ctx.arc(x, y, size / 2, 0, Math.PI * 2)
        break

      case 'square':
        ctx.rect(x - size / 2, y - size / 2, size, size)
        break

      case 'diamond':
        ctx.moveTo(x, y - size / 2)
        ctx.lineTo(x + size / 2, y)
        ctx.lineTo(x, y + size / 2)
        ctx.lineTo(x - size / 2, y)
        ctx.closePath()
        break

      case 'pentagon':
        this.drawPolygon(ctx, x, y, size / 2, 5)
        break

      case 'hexagon':
        this.drawPolygon(ctx, x, y, size / 2, 6)
        break

      default:
        ctx.arc(x, y, size / 2, 0, Math.PI * 2)
    }

    ctx.fill()
  }

  private drawPolygon(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    radius: number,
    sides: number
  ): void {
    ctx.moveTo(x + radius * Math.cos(0), y + radius * Math.sin(0))
    for (let i = 1; i <= sides; i++) {
      const angle = (i * 2 * Math.PI) / sides - Math.PI / 2
      ctx.lineTo(x + radius * Math.cos(angle), y + radius * Math.sin(angle))
    }
    ctx.closePath()
  }

  getOutputSize(
    imageWidth: number,
    imageHeight: number,
    settings: FullRenderSettings
  ): { width: number; height: number } {
    const cellSize = Math.max(4, settings.halftoneSize * 10)
    return {
      width: Math.floor(imageWidth / cellSize) * cellSize,
      height: Math.floor(imageHeight / cellSize) * cellSize,
    }
  }

  private getImageData(source: HTMLImageElement | HTMLCanvasElement | ImageData): ImageData {
    if (source instanceof ImageData) return source
    const canvas = document.createElement('canvas')
    canvas.width = source.width
    canvas.height = source.height
    const ctx = canvas.getContext('2d')!
    ctx.drawImage(source, 0, 0)
    return ctx.getImageData(0, 0, canvas.width, canvas.height)
  }
}

export const halftoneEngine = new HalftoneEngine()